/**
 * PATH: packages/kanban-host/src/allocation/AllocationChip.tsx
 * FILE_ID: KANBANHOST-ALLOCATIONCHIP-003
 * PURPOSE: Canonical body of this kanban-allocation module.
 *          Lifted from apps/<tenant>/src/features/kanban/allocation/AllocationChip.tsx
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md K4).
 * WHY:     Removes the previous tenant-side duplication of
 *          this byte-identical body. One source of truth, both
 *          tenants consume from this package via thin re-export
 *          shims at apps/<tenant>/src/features/kanban/<rel>.
 *
 * v003 (2026-05-26):
 *   Operator feedback on the v002 inline tick/cross: "the button
 *   here and AI tag the tick and cross are bugging and outside of
 *   card rails.. and also the AI tag and structure and
 *   representation can be better.. placements weird.. use first
 *   principles of design and structure to improve this properly".
 *
 *   Root cause of v002's "outside the card rails" overflow:
 *     The root element was `inline-flex` with NO `max-w-full`, and
 *     held TWO sibling bordered pills (the trigger button + a
 *     separate tick/cross block). Inside the EmailRow's flex-wrap
 *     row, the inline-flex parent was treated as a single flex
 *     item, but its own width was unbounded — so on long board
 *     names the tick/cross block was pushed beyond the 380px
 *     inbox column. Two pills also visually fragmented one
 *     conceptual unit (`one allocation` = `one affordance`).
 *
 *   v003 first-principles redesign:
 *     1. ONE bordered pill per allocation. The trigger button + the
 *        tick/cross live inside the SAME rounded-full border,
 *        separated by a vertical divider. No more "two floating
 *        chips standing next to each other".
 *     2. The pill's border colour encodes the source (amber = AI
 *        pending, slate = RULE, default = manual / locked). The
 *        redundant inline "AI" text pill is dropped — colour +
 *        Sparkles prefix carries the signal more compactly.
 *     3. Root is `inline-flex max-w-full min-w-0` so the chip
 *        truncates inside its flex cell instead of escaping.
 *     4. Label dedup via `normaliseAllocationLabel(...)` collapses
 *        duplicate segments like "#384 — Hartley House — Hartley
 *        House" (a frequent Jonas-bridge artifact) down to the
 *        readable "#384 — Hartley House". Lane is appended only
 *        when it's not already implied by the board name.
 *     5. Action zone (✓ ✗) only renders when the operator can act
 *        (AI pending OR RULE). Locked / manual rows show just the
 *        trigger + chevron — no empty divider, no zero-button
 *        wasted space.
 * RELATED:
 *   - apps/<tenant>/src/features/kanban/allocation/AllocationChip.tsx: re-export shim
 *   - packages/email-hub/src/_stubs/kanban/*: email-hub side
 *     mirrors that depend on these symbols.
 *   - packages/ui/src/kanban/api.ts: KanbanApi.acceptAllocation
 *   - email_kanban/views/allocations.py: BE counterpart action
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import { createPortal } from 'react-dom'
import { ArrowRightLeft, Check, ChevronDown, ExternalLink, Kanban, Plus, RotateCcw, Sparkles, X } from 'lucide-react'

import { cn } from '@quotech/tenant-shared/lib/utils'
// K6 follow-up — switched from `@quotech/ui`'s ToastProvider (which
// is NOT mounted by SAM/Newmark; SAM uses its own `<ToastProvider>`
// from apps/sam/src/shared/components/Toast.tsx that bridges into
// the notification bell) to the kanban-host DI seam. The DI seam
// delegates to whatever `useToast` the tenant boot registered via
// setKanbanUseToast(), so AllocationChip toasts now actually appear
// AND mirror to the bell. Pre-K6 these toasts were silent no-ops in
// production — see EMAIL_KANBAN_CUTOVER_ARC.md K6 section.
import { useToast } from '../_internal/notify/useToast'

import { computePopoverPosition } from './AllocateChip'
import { ALLOCATION_MENU_Z_CLASS } from './allocationPopoverLayer'
import { AllocationWhyPopover } from './AllocationWhyPopover'
import { MoveToBoardPicker } from './MoveToBoardPicker'

import type {
  EmailAllocationSummary,
} from '@quotech/tenant-shared/email/api'

import {
  type KanbanApi,
} from '@quotech/ui/kanban'
import { useQueryClient } from '@tanstack/react-query'

import { useKanbanBoardLanes } from '../index'
import {
  buildInvalidationKeys,
  useKanbanQuickAllocate,
} from '../index'

export interface AllocationChipProps {
  emailId: string
  summary: EmailAllocationSummary
  api: KanbanApi
  orgId: number | null
  /** Called when the operator presses "Open card". When omitted the
   *  affordance hides. */
  onOpenCard?: (cardId: string) => void
  /** Called to navigate to the allocated BOARD (operator 2026-06-18 —
   *  "close the full parity": the email card's board tag opens the board
   *  on click, so the reader's chip board label should too). When supplied:
   *    - clicking the board NAME opens the board (the change-lane dropdown
   *      moves to a dedicated chevron button), AND
   *    - an "Open board" row appears in the dropdown.
   *  When omitted (inbox / folder rows, which already have a clickable
   *  frame tag), the chip is unchanged — the whole pill opens the dropdown. */
  onOpenBoard?: () => void
  /** Tenant refetch — called after any mutation succeeds. */
  onChanged?: () => void
  /** Compact placement — used when the row is already FRAMED by this
   *  same board (the inbox card's top frame carries the board name and
   *  the AI ✓/✗ ride the frame's top border). In compact mode the chip
   *  drops the duplicated board name (shows just the lane/stage) and
   *  hides its own inline AI tick/cross, so it's a small "current stage
   *  + change-lane dropdown" handle instead of a second full pill that
   *  repeats the frame. Operator: "you are repeating with wasted space
   *  here". Defaults to false (full pill — e.g. in the reader pane).
   *
   *  Do NOT use compact in the reader FILED lane: there is no board frame
   *  there, so lane-only labels read as orphan tags ("Priority") and hide
   *  which board the email is on (change #431). Prefer hideReviewControls
   *  when you only need to suppress tick/cross. */
  compact?: boolean
  /**
   * Hide inline AI ✓/✗ without switching to the lane-only label. Used by
   * the reader when the decision line owns confirm/reject (change #431).
   * Independent of `compact` so board · lane stays visible.
   */
  hideReviewControls?: boolean
  /**
   * Prefer wrapping the board/lane label onto extra lines instead of
   * truncating into an unreadable sliver. Used by the Placement Manager
   * (BUG-2026-08-03-PLACEMENT-MGR-CHIP-SQUASH) where the dialog has room.
   * Inbox/list rows keep truncate.
   */
  wrapLabel?: boolean
}

// ──────────────────────────────────────────────────────────
// Pure helpers (I → O)
// ──────────────────────────────────────────────────────────

/**
 * Whether the allocation is awaiting an operator decision.
 * Pure I → O so we can unit-test the AI-band gate without rendering.
 */
export function isAiPending(summary: EmailAllocationSummary): boolean {
  return summary.source === 'AI_SUGGESTED_PENDING'
}

/**
 * Whether the allocation came from the deterministic rule matcher
 * (NOT AI, NOT operator-manual). Rule allocations don't need a tick
 * (the rule already accepted the email), but they DO need a one-click
 * cross so the operator can revoke a false positive without opening
 * the dropdown — qb!2847 audit revealed a 40% historical
 * false-positive rate on the Newmark rule matcher, and the operator
 * complaint that motivated the inline cross was: "the fucking
 * investment recap was allocated to the wrong job board by itself..
 * and it doesn't even have the tick and cross to fix this".
 *
 * Pure I → O so we can unit-test the gate without rendering.
 */
export function isRuleAllocation(summary: EmailAllocationSummary): boolean {
  return summary.source === 'RULE'
}

/**
 * EK-14 — gate for the "supersedes" learning signal.
 *
 * Returns ``true`` when the prior allocation was AI-originated and
 * therefore overriding it should write a ``ClassificationCorrection``
 * row for the corpus. ``RULE`` and ``USER_MANUAL`` priors produce
 * no signal (rule is deterministic, manual→manual is just re-routing).
 *
 * Mirrors the BE's ``should_record_supersedes_correction`` gate so
 * both sides agree on what counts as training data. Pure I → O so
 * we can unit-test the gate without rendering React.
 */
export function shouldRecordSupersedes(
  priorSource: EmailAllocationSummary['source'],
): boolean {
  return (
    priorSource === 'AI_AUTO' ||
    priorSource === 'AI_SUGGESTED_PENDING' ||
    priorSource === 'AI_SUGGESTED_USER_CONFIRMED'
  )
}

/**
 * Friendly source label rendered in the dropdown footer. Pure
 * I → O — every branch is tested.
 */
export function describeSource(source: EmailAllocationSummary['source']): string {
  switch (source) {
    case 'AI_AUTO':
      return 'AI auto-allocated'
    case 'AI_SUGGESTED_PENDING':
      return 'AI suggested — awaiting your tick/cross'
    case 'AI_SUGGESTED_USER_CONFIRMED':
      return 'AI suggested → confirmed by user'
    case 'USER_MANUAL':
      return 'Manually allocated'
    case 'RULE':
      return 'Allocated by rule'
    default:
      return 'Allocated'
  }
}

/**
 * v003 — collapse the noise out of bridge-synthesised board/lane
 * names BEFORE they reach the truncate logic.
 *
 * Why this exists: the Jonas bridge constructs Newmark board names
 * by concatenating the job-id, the project name, and the project
 * name again (see `kanban_bridge/jonas/build_board_label.py`).
 * Rows therefore frequently arrive at the FE with names like
 * "#384 — Hartley House — Hartley House", which then collide with
 * the lane name "Site" to render as
 * "#384 — Hartley House — Hartley House · Site". With a 110px
 * truncate window the operator sees "#384 — Hartl…ouse · Site" —
 * unreadable, and clearly duplicated.
 *
 * `normaliseAllocationLabel` is a pure I → O so we can unit-test
 * the dedup table without rendering. It:
 *   1. Splits the board name on en-dash / em-dash / hyphen
 *      separators (the bridge's join convention).
 *   2. Collapses consecutive identical segments.
 *   3. Re-joins with the canonical em-dash separator.
 *   4. Appends the lane name UNLESS the lane is redundant — i.e.
 *      the lane is empty, equals the (cleaned) board name, or is
 *      already the final segment of the cleaned board name.
 *
 * Returns the cleaned board, the cleaned lane, AND the
 * pre-composed combined label so the render layer doesn't have to
 * re-implement the join logic.
 *
 * Sub-board breadcrumb (operator 2026-06-14 "no sub board tagging in
 * emails"): when ``parentBoardName`` is supplied the allocated board
 * is a SUB-board, so we also emit ``parentLabel`` and a ``breadcrumb``
 * that prefixes the parent with a ``›`` separator — "My Inbox ›
 * Triage · Lane". ``breadcrumb`` falls back to ``combined`` when there
 * is no parent (or the parent is redundant against the board), so
 * renderers can ALWAYS read ``breadcrumb`` and get the right thing.
 */
export function normaliseAllocationLabel(
  boardName: string | null | undefined,
  laneName: string | null | undefined,
  parentBoardName?: string | null | undefined,
): {
  boardLabel: string
  laneLabel: string
  parentLabel: string
  combined: string
  breadcrumb: string
} {
  const board = _dedupeSegments(boardName ?? '')
  const lane = (laneName ?? '').trim()
  const parent = _dedupeSegments(parentBoardName ?? '')
  const redundant = _isRedundantLane(board, lane)
  const combined = !board && !lane
    ? ''
    : !board
      ? lane
      : redundant
        ? board
        : `${board} · ${lane}`
  // Prefix the parent only when it's present AND not already the board
  // itself (a self-parent or a duplicate would just be noise).
  const showParent =
    parent.length > 0 &&
    parent.toLowerCase() !== board.toLowerCase() &&
    combined.length > 0
  const breadcrumb = showParent ? `${parent} › ${combined}` : combined
  return { boardLabel: board, laneLabel: lane, parentLabel: parent, combined, breadcrumb }
}

function _dedupeSegments(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return ''
  // Match the bridge layer's join convention: en-dash, em-dash, or
  // a plain hyphen surrounded by whitespace. We don't split on bare
  // hyphens (e.g. "AT-001") to avoid breaking job codes.
  const parts = trimmed.split(/\s+[—–-]\s+/)
  const out: string[] = []
  for (const raw of parts) {
    const part = raw.trim()
    if (!part) continue
    const prev = out[out.length - 1]
    if (prev !== undefined && prev.toLowerCase() === part.toLowerCase()) continue
    out.push(part)
  }
  return out.join(' — ')
}

function _isRedundantLane(board: string, lane: string): boolean {
  if (!lane) return true
  const b = board.toLowerCase()
  const l = lane.toLowerCase()
  if (b === l) return true
  // Final segment of the board (after our em-dash join) already
  // names the lane — e.g. board "Sales — Hot leads", lane "Hot
  // leads" → don't re-append.
  if (b.endsWith(` — ${l}`)) return true
  if (b.endsWith(` · ${l}`)) return true
  return false
}

// ──────────────────────────────────────────────────────────
// Inline "+ New lane" row (FEAT-2026-06-23-INBOX-ADD-LANE)
// ──────────────────────────────────────────────────────────
//
// Operator (2026-06-23): "I cannot change lane in my inbox ... where is
// the option to add new lanes". The change-lane popover listed only the
// board's existing lanes — there was no affordance to add one. This row
// reuses the proven AllocateToKanbanSubMenu.NewLaneInlineRow logic
// (createLane({ board, name, kind: 'CUSTOM', position }) then invalidate
// ['kanban-lanes', boardId]) but is styled to match this dropdown's
// plain-button rows. ``onCreated`` drops the current email into the
// freshly-created lane so the action completes in one step.
function InlineNewLaneRow({
  api,
  boardId,
  nextPosition,
  busy,
  onCreated,
}: {
  api: KanbanApi
  boardId: string
  nextPosition: number
  busy: boolean
  onCreated: (laneId: string) => void
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const reset = () => {
    setEditing(false)
    setName('')
    setError(null)
  }

  const submit = async () => {
    const trimmed = name.trim()
    if (!trimmed || saving) return
    setSaving(true)
    setError(null)
    try {
      // Default kind CUSTOM — mirrors AddLaneButton / NewLaneInlineRow.
      // The model's 30+ lane kinds are mostly internal routing state;
      // CUSTOM is the neutral "just a list" kind for ad-hoc creation.
      const lane = await api.createLane({
        board: boardId,
        name: trimmed,
        kind: 'CUSTOM',
        position: nextPosition,
      })
      await queryClient.invalidateQueries({
        queryKey: ['kanban-lanes', boardId],
      })
      setSaving(false)
      reset()
      onCreated(lane.id)
    } catch (err) {
      setError((err as Error)?.message || 'Could not create lane')
      setSaving(false)
    }
  }

  if (!editing) {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => setEditing(true)}
        className="flex items-center gap-2 w-full px-2 py-1 text-[11px] text-left rounded transition-colors hover:bg-accent disabled:opacity-50"
      >
        <Plus className="h-3 w-3 text-muted-foreground" />
        <span>New lane…</span>
      </button>
    )
  }

  return (
    <div className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            void submit()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            reset()
          }
        }}
        placeholder="New lane name…"
        disabled={saving}
        className="w-full rounded-md border border-border bg-background px-2 py-1 text-[11px] text-foreground outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
      />
      {error && <div className="mt-1 text-[10px] text-red-500">{error}</div>}
      <div className="mt-1 flex items-center gap-1">
        <button
          type="button"
          disabled={saving || !name.trim()}
          onClick={() => void submit()}
          className="rounded-md bg-blue-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
        >
          {saving ? 'Adding…' : 'Add lane'}
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={reset}
          className="rounded-md px-2 py-1 text-[10px] text-muted-foreground hover:bg-accent"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────

export function AllocationChip({
  emailId,
  summary,
  api,
  orgId,
  onOpenCard,
  onOpenBoard,
  onChanged,
  compact = false,
  hideReviewControls = false,
  wrapLabel = false,
}: AllocationChipProps) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  // Optimistic self-dismiss. Once the operator rejects (AI ✗) or
  // revokes this allocation, hide the chip in the SAME frame as the
  // click instead of waiting for the parent's refetch to drop
  // `email.allocation_summary`. Operator report (2026-06-15): "I
  // crossed the AI tag … it's still as is unless I refresh." The
  // server call already archives the card / records the correction —
  // this just kills the visual lag. Reset when a DIFFERENT allocation
  // lands on the same row (see the `summary.allocation_id` effect
  // below) so a fresh suggestion re-shows.
  const [dismissed, setDismissed] = useState(false)
  // Which face of the dropdown is showing. 'lanes' is the default
  // (change-lane-within-this-board); 'boards' swaps in the
  // MoveToBoardPicker so the operator can re-allocate to a DIFFERENT
  // board (operator report 2026-05-29 — the chip was stuck on the
  // wrong duplicate board with no way to leave it).
  const [view, setView] = useState<'lanes' | 'boards'>('lanes')
  const [whyAt, setWhyAt] = useState<{ x: number; y: number } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  // BUG-2026-07-17-ALLOCATION-DROPDOWN-CLIPPED — the dropdown renders in
  // a <body> portal (the reader Signal Strip wraps this chip in
  // overflow-hidden, which clipped the old in-flow `absolute top-full`
  // menu to a sliver, blocking change-lane / move-board entirely).
  // Mirrors the sibling AllocateChip portal (computePopoverPosition).
  const popoverRef = useRef<HTMLDivElement>(null)
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null)
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const reposition = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const size = {
      width: popoverRef.current?.offsetWidth || 240,
      height: popoverRef.current?.offsetHeight || 300,
    }
    setAnchor(
      computePopoverPosition(
        { top: r.top, bottom: r.bottom, left: r.left },
        size,
        { width: window.innerWidth, height: window.innerHeight },
      ),
    )
  }, [])

  useEffect(() => {
    if (!open) {
      setAnchor(null)
      return
    }
    reposition()
    // capture:true so scrolls on inner reader columns retrack the menu,
    // not just window scroll.
    const onMove = () => reposition()
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open, view, reposition])

  const ai = isAiPending(summary)
  const rule = isRuleAllocation(summary)
  const boardSwatch = summary.board_color ?? '#94a3b8'

  // v003 — pre-compute the human-readable label once per render so
  // the trigger button and the title attribute stay in sync.
  const label = useMemo(
    () =>
      normaliseAllocationLabel(
        summary.board_name,
        summary.lane_name,
        summary.parent_board_name,
      ),
    [summary.board_name, summary.lane_name, summary.parent_board_name],
  )

  // In compact placement the board name is already on the row's frame,
  // so we show only the lane/stage (e.g. "Site") to avoid repeating it.
  // Fall back to the combined label when there's no distinct lane.
  // In full placement use the breadcrumb so a sub-board allocation reads
  // "My Inbox › Triage · Lane" (operator: "no sub board tagging"); the
  // breadcrumb collapses to ``combined`` when there's no parent.
  const triggerLabel = compact
    ? label.laneLabel || label.combined
    : label.breadcrumb

  // Close on outside click.
  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(open, () => setOpen(false), [containerRef, popoverRef])

  // Always reopen on the lane list — never strand the operator inside
  // the board picker after they close + reopen the dropdown.
  useEffect(() => {
    if (!open) setView('lanes')
  }, [open])

  // Re-show the chip if the row receives a NEW allocation after a
  // local dismiss (e.g. operator rejected, then a rule / manual move
  // re-allocated it). Keyed on allocation_id so a confirm (same id)
  // never accidentally un-dismisses a rejected row.
  useEffect(() => {
    setDismissed(false)
  }, [summary.allocation_id])

  // Perf (operator report 2026-06-10 "it took a time to load boards"):
  // prefetch the full board list the MOMENT the dropdown opens, so the
  // "Move to another board" view is already warm by the time the
  // operator clicks into it. Shares the exact ['kanban-boards'] key +
  // staleTime as useAllocateToKanbanMenuData, so MoveToBoardPicker
  // reads straight from cache instead of paying the round-trip on
  // first render. No-op (deduped) when the cache is already fresh.
  useEffect(() => {
    if (!open) return
    // Key + filter must mirror useAllocateToKanbanMenuData exactly or
    // the prefetch warms a key MoveToBoardPicker never reads
    // (BUG-2026-07-15-ALLOCATE-PICKER-CROSS-ORG org scoping).
    void queryClient.prefetchQuery({
      queryKey: orgId != null ? ['kanban-boards', orgId] : ['kanban-boards'],
      queryFn: () =>
        api.listBoards(orgId != null ? { organisationId: orgId } : undefined),
      staleTime: 30_000,
    })
  }, [open, queryClient, api, orgId])

  const refreshCaches = useCallback(() => {
    for (const key of buildInvalidationKeys()) {
      void queryClient.invalidateQueries({ queryKey: [...key] })
    }
    onChanged?.()
  }, [queryClient, onChanged])

  // ── Quick-allocate hook reused for "Change lane" (same board)
  const quickAllocate = useKanbanQuickAllocate({
    api,
    orgId,
    notify: (kind, message) => toast(message, kind),
  })

  // ── AI tick / cross
  const acceptAi = useCallback(async () => {
    if (busy) return
    setBusy(true)
    try {
      await api.confirmAiAllocation(summary.allocation_id)
      toast('AI allocation confirmed — visibility propagated.', 'success')
      refreshCaches()
    } catch (err) {
      toast(`Could not confirm: ${(err as Error).message || 'error'}`, 'error')
    } finally {
      setBusy(false)
    }
  }, [api, busy, summary.allocation_id, toast, refreshCaches])

  const rejectAi = useCallback(async () => {
    if (busy) return
    setBusy(true)
    try {
      await api.rejectAiAllocation(summary.allocation_id, {
        reason: 'Operator rejected from row chip',
      })
      // Hide immediately — don't wait for refreshCaches' refetch to
      // drop the summary (that lag is the bug we're fixing).
      setDismissed(true)
      toast('AI allocation rejected — card archived.', 'success')
      refreshCaches()
    } catch (err) {
      toast(`Could not reject: ${(err as Error).message || 'error'}`, 'error')
    } finally {
      setBusy(false)
    }
  }, [api, busy, summary.allocation_id, toast, refreshCaches])

  // ── Revoke
  const revoke = useCallback(async () => {
    if (busy) return
    setBusy(true)
    try {
      await api.revokeAllocation(summary.allocation_id, 'Revoked from row chip')
      // Hide immediately (same rationale as rejectAi).
      setDismissed(true)
      toast('Removed from Kanban.', 'success')
      refreshCaches()
    } catch (err) {
      toast(`Could not revoke: ${(err as Error).message || 'error'}`, 'error')
    } finally {
      setBusy(false)
      setOpen(false)
    }
  }, [api, busy, summary.allocation_id, toast, refreshCaches])

  // ── Accept RULE / AI_AUTO allocation (operator-blesses it).
  // Calls the POST /allocations/<id>/accept/ endpoint added 2026-05-25
  // in response to operator request "There should be a clear tick and
  // cross to accept this" — the RULE chip previously rendered only ✗
  // revoke with no ratify affordance.
  //
  // Backwards-compatible against KanbanApi mocks that pre-date the new
  // method: if the impl doesn't expose acceptAllocation we surface a
  // clear toast instead of crashing.
  const acceptOperator = useCallback(async () => {
    if (busy) return
    if (typeof api.acceptAllocation !== 'function') {
      toast(
        'Accept is not available on this Kanban API instance.',
        'error',
      )
      return
    }
    setBusy(true)
    try {
      await api.acceptAllocation(summary.allocation_id)
      toast('Confirmed — locked in.', 'success')
      refreshCaches()
    } catch (err) {
      toast(`Could not accept: ${(err as Error).message || 'error'}`, 'error')
    } finally {
      setBusy(false)
    }
  }, [api, busy, summary.allocation_id, toast, refreshCaches])

  // ── Same-board lane list for "Change lane"
  const lanes = useKanbanBoardLanes({
    api,
    boardId: summary.board_id,
    enabled: open,
  })

  const sortedLanes = useMemo(() => {
    return lanes.lanes
      .filter((l) => !l.archived)
      .slice()
      .sort((a, b) => a.position - b.position)
  }, [lanes.lanes])

  // Position for an operator-created lane — after the current max so it
  // slots in at the end. Computed off the FULL lane list (incl. archived)
  // so we never collide with a hidden lane's position.
  const nextLanePosition = useMemo(() => {
    const positions = lanes.lanes.map((l) => l.position ?? 0)
    return (positions.length ? Math.max(...positions) : 0) + 10
  }, [lanes.lanes])

  const moveToLane = useCallback(
    async (laneId: string) => {
      if (busy || laneId === summary.lane_id) return
      setBusy(true)
      try {
        // EK-14 — when the current allocation is AI-originated, pass
        // it as `supersedesAllocationId` so the BE writes a
        // ClassificationCorrection row capturing the override.
        // Without this, moving from "AI suggested · Triage" to "AI
        // suggested · Site" (same board) loses the lane-level
        // correction signal entirely.
        //
        // Pure check via `shouldRecordSupersedes` so the test suite
        // can pin the gate without mounting React. (See
        // useKanbanQuickAllocate.test.ts.)
        const supersedesAllocationId = shouldRecordSupersedes(summary.source)
          ? summary.allocation_id
          : undefined
        const outcome = await quickAllocate.run({
          emailId,
          laneId,
          boardId: summary.board_id,
          source: 'USER_MANUAL',
          supersedesAllocationId,
        })
        if (outcome.kind === 'ok') {
          setOpen(false)
        }
        refreshCaches()
      } finally {
        setBusy(false)
      }
    },
    [
      busy, emailId, quickAllocate, refreshCaches,
      summary.allocation_id, summary.board_id, summary.lane_id, summary.source,
    ],
  )

  // ── Move to a DIFFERENT board (lands on that board's default lane).
  // Mirrors moveToLane's supersedes-learning gate: when the prior
  // allocation was AI-originated, overriding it to another board is a
  // correction worth recording. The MoveToBoardPicker resolves the
  // target lane (left-most non-archived); we just perform the mutation
  // here because this component owns emailId + the supersedes gate.
  const moveToBoard = useCallback(
    async (boardId: string, laneId: string) => {
      if (busy) return
      if (boardId === summary.board_id && laneId === summary.lane_id) {
        setOpen(false)
        return
      }
      setBusy(true)
      try {
        const supersedesAllocationId = shouldRecordSupersedes(summary.source)
          ? summary.allocation_id
          : undefined
        const outcome = await quickAllocate.run({
          emailId,
          laneId,
          boardId,
          source: 'USER_MANUAL',
          supersedesAllocationId,
        })
        if (outcome.kind === 'ok') {
          setOpen(false)
        }
        refreshCaches()
      } finally {
        setBusy(false)
      }
    },
    [
      busy, emailId, quickAllocate, refreshCaches,
      summary.allocation_id, summary.board_id, summary.lane_id, summary.source,
    ],
  )

  // v003 — the pill border encodes the source so we can drop the
  // separate "AI" text badge inside the chip body. amber = AI
  // pending, slate = deterministic rule, default = manual / locked.
  const pillBorder = ai
    ? 'border-amber-400/70 bg-amber-50/60 dark:bg-amber-950/20'
    : rule
      ? 'border-slate-400/70 bg-slate-50/60 dark:bg-slate-800/40'
      : 'border-border bg-card'
  const dividerBorder = ai
    ? 'border-amber-400/60'
    : rule
      ? 'border-slate-400/60'
      : 'border-border'
  const triggerText = ai
    ? 'text-amber-900 dark:text-amber-100'
    : 'text-foreground/80'

  // Optimistically removed by a reject/revoke this render cycle.
  // Render nothing until the parent's refetch confirms (which unmounts
  // us by dropping `allocation_summary`). Placed AFTER every hook so we
  // never break the rules-of-hooks ordering.
  if (dismissed) return null

  return (
    // v003 — `max-w-full min-w-0` is the critical fix for the
    // "chip escapes the inbox column rails" bug. The previous
    // root had no width cap, so on long board names (Newmark /
    // Bravada job titles routinely exceed 40 chars) the chip
    // + tick/cross block grew beyond the parent flex cell and
    // visually clipped the next row's content.
    <div ref={containerRef} className="relative inline-flex max-w-full min-w-0">
      {/* v003 — ONE bordered pill. Trigger + actions live INSIDE the
          same rounded-full border, separated by a thin vertical
          divider when actions are present. */}
      <div
        className={cn(
          'inline-flex items-stretch min-w-0 max-w-full border overflow-hidden',
          'transition-colors',
          // Wrap mode: rounded-md so multi-line labels do not fight a
          // capsule clip. Truncate mode keeps the capsule pill.
          wrapLabel ? 'rounded-md' : 'rounded-full',
          pillBorder,
          busy && 'opacity-60 cursor-wait',
        )}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            // Parity with the inbox card's board tag: when the host wires
            // onOpenBoard, clicking the board NAME opens the board (the
            // dropdown moves to the dedicated chevron button beside it).
            // Otherwise the whole pill keeps toggling the dropdown.
            if (onOpenBoard) onOpenBoard()
            else setOpen((o) => !o)
          }}
          onContextMenu={(e) => {
            // Right-click → "Why this board?" popover. Stop propagation
            // so the row-level context menu (kanban → email) doesn't
            // open on top of us.
            e.preventDefault()
            e.stopPropagation()
            setWhyAt({ x: e.clientX, y: e.clientY })
          }}
          disabled={busy}
          className={cn(
            'inline-flex items-center gap-1 min-w-0 max-w-full px-2 py-0.5 text-[10px] font-medium',
            'hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'transition-colors',
            wrapLabel && 'items-start py-1',
            triggerText,
            onOpenBoard && 'hover:underline',
          )}
          title={
            onOpenBoard
              ? `Open board · ${label.breadcrumb} (right-click for AI reasoning)`
              : `${label.breadcrumb} — ${describeSource(summary.source)} (right-click for AI reasoning)`
          }
        >
          {ai ? (
            <Sparkles className="h-2.5 w-2.5 shrink-0 text-amber-500" aria-hidden />
          ) : (
            <Kanban className="h-2.5 w-2.5 shrink-0 text-muted-foreground" aria-hidden />
          )}
          <span
            className="h-2 w-2 rounded-full shrink-0"
            style={{ backgroundColor: boardSwatch }}
            aria-hidden
          />
          <span
            className={cn(
              'min-w-0',
              wrapLabel
                ? 'whitespace-normal break-words text-left leading-snug'
                : 'truncate',
              // Compact placement is ONLY ever rendered inside an EmailRow
              // (`.group`) footer — the inbox + folder rows, never the reader
              // pane (which uses full mode). So a `group-hover` cap-lift here
              // safely reveals a truncated lane on ROW hover (operator
              // 2026-06-23: "hovering over say … sys… should auto expand it so
              // I can read it fully") without affecting the reader. Capped
              // generously at rest so most lanes show whole and the strip stays
              // compact; only a long lane truncates, and it expands on hover.
              // wrapLabel wins over the compact cap (Placement Manager).
              !wrapLabel &&
                compact &&
                'max-w-[140px] group-hover:max-w-[280px] transition-[max-width] duration-200 ease-out',
            )}
          >
            {triggerLabel}
          </span>
          {/* Chevron stays INSIDE the trigger only when the trigger itself
              toggles the dropdown. When onOpenBoard owns the trigger, the
              chevron becomes its own button below so the dropdown stays
              reachable. */}
          {!onOpenBoard && (
            <ChevronDown className="h-2.5 w-2.5 shrink-0 text-muted-foreground/60" aria-hidden />
          )}
        </button>

        {onOpenBoard && (
          // Dedicated dropdown toggle — change lane / move board / open card
          // still live one click away even though the board name now
          // navigates. Same border treatment as the AI/RULE action zone.
          <button
            type="button"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation()
              setOpen((o) => !o)
            }}
            aria-label="Allocation options"
            title="Change lane · move board · open card"
            className={cn(
              'inline-flex items-center justify-center px-1 shrink-0 border-l',
              'hover:bg-accent/40 transition-colors disabled:opacity-40',
              dividerBorder,
              triggerText,
            )}
          >
            <ChevronDown className="h-2.5 w-2.5 shrink-0 text-muted-foreground/60" aria-hidden />
          </button>
        )}

        {ai && !compact && !hideReviewControls && (
          // AI suggestion controls — accept (✓) / reject (✗).
          // v003 moved the controls INSIDE the chip's border so the
          // operator reads "one allocation = one bordered unit"
          // instead of "two bordered pills next to each other that
          // somehow relate". The vertical border-l divider replaces
          // the previous ml-1 gap.
          // hideReviewControls (reader decision line) suppresses these
          // without collapsing the label to lane-only (change #431).
          <div
            className={cn('flex shrink-0 items-stretch border-l', dividerBorder)}
            aria-label="AI suggestion controls"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              disabled={busy}
              onClick={() => void acceptAi()}
              title="Accept AI suggestion"
              aria-label="Accept AI allocation"
              className="inline-flex items-center justify-center w-5 px-1 text-green-600 dark:text-green-400 hover:bg-green-500/15 transition-colors disabled:opacity-40"
            >
              <Check className="h-3 w-3" aria-hidden />
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void rejectAi()}
              title="Reject AI suggestion"
              aria-label="Reject AI allocation"
              className={cn(
                'inline-flex items-center justify-center w-5 px-1 text-red-600 dark:text-red-400 hover:bg-red-500/15 transition-colors disabled:opacity-40 border-l',
                dividerBorder,
              )}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </div>
        )}

        {rule && (
          // Inline tick / cross affordance for RULE allocations. The
          // rule matcher has already "accepted" the email — but
          // (a) the operator must be able to one-click revoke a false
          // positive without opening the dropdown (historical reason
          // for the ✗), AND
          // (b) the operator must be able to one-click RATIFY a
          // correct rule allocation so future rule re-runs / AI
          // re-classification passes can't silently overwrite it.
          // ✓ promotes RULE → USER_MANUAL via
          // POST /allocations/<id>/accept/.
          //
          // v003 moved the controls INSIDE the chip's border (was a
          // separate bordered pill in v002) — same pattern as the
          // AI branch. The slate colour treatment (border-slate-…)
          // is preserved on the pill border itself.
          <div
            className={cn('flex shrink-0 items-stretch border-l', dividerBorder)}
            aria-label="Rule allocation controls"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              disabled={busy}
              onClick={() => void acceptOperator()}
              title="Confirm — lock this allocation in so re-classification can't change it"
              aria-label="Accept rule allocation"
              className="inline-flex items-center justify-center w-5 px-1 text-slate-600 dark:text-slate-300 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-500/15 transition-colors disabled:opacity-40"
            >
              <Check className="h-3 w-3" aria-hidden />
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void revoke()}
              title="Wrong board? Revoke this rule allocation"
              aria-label="Revoke rule allocation"
              className={cn(
                'inline-flex items-center justify-center w-5 px-1 text-slate-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/15 transition-colors disabled:opacity-40 border-l',
                dividerBorder,
              )}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </div>
        )}
      </div>

      {/* Portalled to <body> so the Signal Strip's overflow-hidden (or any
          other clipping ancestor) can never truncate the menu
          (BUG-2026-07-17-ALLOCATION-DROPDOWN-CLIPPED). `invisible` until
          the anchor is measured, matching AllocateChip. */}
      {open &&
        createPortal(
        <div
          ref={popoverRef}
            className={cn(
              // z-[80]: above Placement Manager backdrop/dialog (70/71).
              // BUG-2026-08-03-PLACEMENT-MGR-ALLOCATE-UNDER-BACKDROP
              'fixed min-w-[220px] max-w-[260px] rounded-md border border-border bg-card shadow-lg p-1',
              ALLOCATION_MENU_Z_CLASS,
              !anchor && 'invisible',
            )}
          style={anchor ? { top: anchor.top, left: anchor.left } : { top: 0, left: 0 }}
          onClick={(e) => e.stopPropagation()}
          role="menu"
        >
          {view === 'boards' ? (
            <MoveToBoardPicker
              api={api}
              orgId={orgId}
              currentBoardId={summary.board_id}
              busy={busy}
              onBack={() => setView('lanes')}
              onPick={(boardId, laneId) => void moveToBoard(boardId, laneId)}
            />
          ) : (
          <>
          <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground/70 font-semibold">
            Change lane on {label.boardLabel || summary.board_name}
          </div>
          {lanes.isLoading && sortedLanes.length === 0 && (
            <div className="px-2 py-1 text-[11px] text-muted-foreground">Loading lanes…</div>
          )}
          {lanes.isError && (
            <div className="px-2 py-1 text-[11px] text-red-500">Could not load lanes.</div>
          )}
          {!lanes.isLoading && !lanes.isError && sortedLanes.length === 0 && (
            // Never strand the operator on a blank dropdown ("I clicked
            // and it doesn't show shit"). When a board legitimately has
            // no lanes we say so and still offer the board-move +
            // remove actions below.
            <div className="px-2 py-1 text-[11px] text-muted-foreground">
              This board has no lanes yet.
            </div>
          )}
          {sortedLanes.map((lane) => (
            <button
              key={lane.id}
              type="button"
              disabled={busy}
              onClick={() => void moveToLane(lane.id)}
              className={cn(
                'flex items-center gap-2 w-full px-2 py-1 text-[11px] text-left rounded transition-colors',
                'hover:bg-accent disabled:opacity-50',
                lane.id === summary.lane_id && 'bg-accent/60 font-semibold',
              )}
            >
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: lane.color || '#94a3b8' }}
              />
              <span className="flex-1 truncate">{lane.name}</span>
              {lane.id === summary.lane_id && (
                <span className="text-[9px] text-muted-foreground">current</span>
              )}
            </button>
          ))}
          {/* FEAT-2026-06-23-INBOX-ADD-LANE (operator: "where is the
              option to add new lanes"). Inline "+ New lane" so the
              operator can create a lane right here and drop THIS email
              into it — mirrors the proven AllocateToKanbanSubMenu
              NewLaneInlineRow flow (createLane + invalidate
              ['kanban-lanes', boardId]), styled to match this dropdown.
              Hidden only while the lane list is still loading. */}
          {!lanes.isLoading && !lanes.isError && (
            <InlineNewLaneRow
              api={api}
              boardId={summary.board_id}
              nextPosition={nextLanePosition}
              busy={busy}
              onCreated={(laneId) => void moveToLane(laneId)}
            />
          )}
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            disabled={busy}
            onClick={() => setView('boards')}
            className="flex items-center gap-2 w-full px-2 py-1 text-[11px] text-left rounded transition-colors hover:bg-accent"
          >
            <ArrowRightLeft className="h-3 w-3 text-muted-foreground" />
            <span>Move to another board…</span>
          </button>
          {onOpenBoard && (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setOpen(false)
                onOpenBoard()
              }}
              className="flex items-center gap-2 w-full px-2 py-1 text-[11px] text-left rounded transition-colors hover:bg-accent"
            >
              <ExternalLink className="h-3 w-3 text-muted-foreground" />
              <span>Open board</span>
            </button>
          )}
          {onOpenCard && (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setOpen(false)
                onOpenCard(summary.card_id)
              }}
              className="flex items-center gap-2 w-full px-2 py-1 text-[11px] text-left rounded transition-colors hover:bg-accent"
            >
              <Kanban className="h-3 w-3 text-muted-foreground" />
              <span>Card</span>
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => void revoke()}
            className="flex items-center gap-2 w-full px-2 py-1 text-[11px] text-left rounded transition-colors hover:bg-red-500/10 text-red-600 dark:text-red-400"
          >
            <RotateCcw className="h-3 w-3" />
            <span>Remove from Kanban</span>
          </button>
          <div className="border-t border-border my-1" />
          <div className="px-2 py-1 text-[10px] text-muted-foreground/80">
            {describeSource(summary.source)}
          </div>
          </>
          )}
        </div>,
        document.body,
      )}

      {whyAt && (
        <AllocationWhyPopover
          api={api}
          allocationId={summary.allocation_id}
          open={Boolean(whyAt)}
          onClose={() => setWhyAt(null)}
          x={whyAt.x}
          y={whyAt.y}
        />
      )}
    </div>
  )
}
